import apiClient, { getErrorMessage } from './client';

interface ResultEnvelope<T> {
  isSuccess: boolean;
  data?: T;
  error?: string | null;
}

function unwrapSuccess(res: { data: unknown }): void {
  const raw = res.data;
  if (raw !== null && typeof raw === 'object' && 'isSuccess' in raw) {
    const e = raw as ResultEnvelope<unknown>;
    if (!e.isSuccess) {
      throw new Error((e.error ?? 'Request failed').toString());
    }
    return;
  }
  throw new Error('Unexpected response');
}

function unwrapData<T>(res: { data: unknown }): T {
  const raw = res.data;
  if (raw !== null && typeof raw === 'object' && 'isSuccess' in raw) {
    const e = raw as ResultEnvelope<T>;
    if (!e.isSuccess) {
      throw new Error((e.error ?? 'Request failed').toString());
    }
    if (e.data === undefined) {
      throw new Error('Empty data');
    }
    return e.data;
  }
  return raw as T;
}

export interface ComplianceNoteDto {
  id: string;
  clientUserId: string;
  body: string;
  createdByAdminUserId: string;
  createdAt: string;
}

export interface ClientReminderDto {
  id: string;
  clientUserId: string;
  title: string;
  description?: string | null;
  dueAt: string;
  completedAt?: string | null;
  createdByAdminUserId: string;
  createdAt: string;
}

export interface RegeneratedClientPasswordDto {
  plainPassword?: string | null;
  emailSent: boolean;
}

export const portalClientsApi = {
  setStatus: async (clientUserId: string, status: string, reason?: string): Promise<void> => {
    try {
      const payload: { status: string; reason?: string } = { status };
      const trimmed = reason?.trim();
      if (trimmed) payload.reason = trimmed;
      const res = await apiClient.put(`/admin/portal-clients/${clientUserId}/status`, payload);
      unwrapSuccess(res);
    } catch (e: unknown) {
      throw new Error(getErrorMessage(e));
    }
  },

  updateEmail: async (clientUserId: string, email: string): Promise<void> => {
    try {
      const res = await apiClient.put(`/admin/portal-clients/${clientUserId}/email`, { email });
      unwrapSuccess(res);
    } catch (e: unknown) {
      throw new Error(getErrorMessage(e));
    }
  },

  listNotes: async (clientUserId: string): Promise<ComplianceNoteDto[]> => {
    try {
      const res = await apiClient.get(`/admin/portal-clients/${clientUserId}/compliance-notes`);
      return unwrapData<ComplianceNoteDto[]>(res);
    } catch (e: unknown) {
      throw new Error(getErrorMessage(e));
    }
  },

  addNote: async (clientUserId: string, body: string): Promise<string> => {
    try {
      const res = await apiClient.post(`/admin/portal-clients/${clientUserId}/compliance-notes`, { body });
      return unwrapData<{ id: string }>(res).id;
    } catch (e: unknown) {
      throw new Error(getErrorMessage(e));
    }
  },

  listReminders: async (clientUserId: string): Promise<ClientReminderDto[]> => {
    try {
      const res = await apiClient.get(`/admin/portal-clients/${clientUserId}/reminders`);
      return unwrapData<ClientReminderDto[]>(res);
    } catch (e: unknown) {
      throw new Error(getErrorMessage(e));
    }
  },

  addReminder: async (
    clientUserId: string,
    payload: { title: string; description?: string | null; dueAt: string }
  ): Promise<string> => {
    try {
      const res = await apiClient.post(`/admin/portal-clients/${clientUserId}/reminders`, payload);
      return unwrapData<{ id: string }>(res).id;
    } catch (e: unknown) {
      throw new Error(getErrorMessage(e));
    }
  },

  setReminderCompleted: async (clientUserId: string, reminderId: string, completed: boolean): Promise<void> => {
    try {
      const res = await apiClient.put(
        `/admin/portal-clients/${clientUserId}/reminders/${reminderId}`,
        null,
        { params: { completed } }
      );
      unwrapSuccess(res);
    } catch (e: unknown) {
      throw new Error(getErrorMessage(e));
    }
  },

  regeneratePassword: async (
    clientUserId: string,
    delivery: 'email' | 'show'
  ): Promise<RegeneratedClientPasswordDto> => {
    try {
      const res = await apiClient.post(`/admin/portal-clients/${clientUserId}/regenerate-password`, {
        delivery,
      });
      return unwrapData<RegeneratedClientPasswordDto>(res);
    } catch (e: unknown) {
      throw new Error(getErrorMessage(e));
    }
  },

  setDigitalFeatures: async (
    clientUserId: string,
    digitalIncomingEnabled: boolean,
    digitalOutgoingEnabled: boolean
  ): Promise<void> => {
    try {
      const res = await apiClient.put(`/admin/portal-clients/${clientUserId}/digital-features`, {
        digitalIncomingEnabled,
        digitalOutgoingEnabled,
      });
      unwrapSuccess(res);
    } catch (e: unknown) {
      throw new Error(getErrorMessage(e));
    }
  },
};
